/**
 * Fills in the live reviews.io average rating and review count for the cart drawer reviews badge.
 *
 * Uses the public (no auth, CORS-enabled) merchant reviews endpoint, which returns `stats.total_reviews` and
 * `stats.average_rating`.
 * The result is cached in sessionStorage so cart drawer re-renders don't refetch or flash.
 */
const REVIEWS_API_URL = 'https://api.reviews.io/merchant/reviews';
const CACHE_TTL = 2 * 60 * 60 * 1000; // matches the API's own Cache-Control max-age

const pendingRequests = new Map();

function getCacheKey(store) {
  return `reviewsio-stats:${store}`;
}

function readCachedStats(store) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(getCacheKey(store)));
    return cached && Date.now() - cached.time < CACHE_TTL ? cached.stats : null;
  } catch {
    return null;
  }
}

function fetchStats(store) {
  if (!pendingRequests.has(store)) {
    const request = fetch(`${REVIEWS_API_URL}?store=${encodeURIComponent(store)}&per_page=1`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`reviews.io responded with ${response.status}`);
        }

        return response.json();
      })
      .then(({ stats }) => {
        try {
          sessionStorage.setItem(getCacheKey(store), JSON.stringify({ time: Date.now(), stats }));
        } catch {}

        return stats;
      })
      .catch(() => null);

    pendingRequests.set(store, request);
  }

  return pendingRequests.get(store);
}

class ReviewsCount extends HTMLElement {
  async connectedCallback() {
    const store = this.getAttribute('store');

    if (!store) {
      return;
    }

    const stats = readCachedStats(store) ?? (await fetchStats(store));
    const total = Number(stats?.total_reviews);
    const rating = Number(stats?.average_rating);

    if (!total || !rating) {
      return;
    }

    const locale = document.documentElement.lang || undefined;

    this.querySelector('[data-reviews-rating]').textContent = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rating);
    this.querySelector('[data-reviews-count]').textContent = new Intl.NumberFormat(locale).format(total);
    this.classList.add('is-loaded');
  }
}

if (!window.customElements.get('reviews-count')) {
  window.customElements.define('reviews-count', ReviewsCount);
}
